import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Upload, Camera, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [consultantDetails, setConsultantDetails] = useState({ consultant_name: '', consultant_phone: '' });
  const [savingConsultant, setSavingConsultant] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    fetchConsultantDetails();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        setFormData(prev => ({ ...prev, email: user.email }));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultantDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('recruiter_settings')
        .select('consultant_name, consultant_phone')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setConsultantDetails({
          consultant_name: data.consultant_name || '',
          consultant_phone: data.consultant_phone || ''
        });
      }
    } catch (error) {
      console.error('Error fetching consultant details:', error);
    }
  };

  const handleSaveConsultantDetails = async () => {
    try {
      setSavingConsultant(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('recruiter_settings')
        .upsert({
          user_id: user.id,
          consultant_name: consultantDetails.consultant_name || null,
          consultant_phone: consultantDetails.consultant_phone || null,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Consultant details saved' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSavingConsultant(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordReset = async () => {
    try {
      setUpdating(true);
      setMessage(null);

      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password reset email sent! Check your inbox.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    try {
      setUpdating(true);
      setMessage(null);

      const { error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully' });
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Profile</h3>
        <p className="text-sm text-muted-foreground">
          Manage your account settings and profile information
        </p>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-destructive' : 'border-green-500'}>
          <AlertDescription className={message.type === 'error' ? 'text-destructive' : 'text-green-700'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Picture */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Picture</CardTitle>
          <CardDescription>
            Upload a profile picture to personalize your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center">
                <User className="h-12 w-12 text-muted-foreground" />
              </div>
              <Button
                size="icon"
                variant="outline"
                className="absolute bottom-0 right-0 rounded-full h-8 w-8"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                JPG, PNG or GIF. Max size 2MB.
              </p>
              <Button variant="outline" size="sm" disabled>
                <Upload className="h-4 w-4 mr-2" />
                Upload Image (Coming Soon)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Address */}
      <Card>
        <CardHeader>
          <CardTitle>Email Address</CardTitle>
          <CardDescription>
            Your account email address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="flex gap-2">
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                disabled
                className="flex-1"
              />
              <Button variant="outline" disabled>
                Change Email (Coming Soon)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consultant Details */}
      <Card>
        <CardHeader>
          <CardTitle>Consultant Details</CardTitle>
          <CardDescription>
            Shown as the point of contact on documents you generate for clients (e.g. indicative candidate profiles) -
            no name field exists on your account otherwise, only your login email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="consultant_name">Name</Label>
            <Input
              id="consultant_name"
              value={consultantDetails.consultant_name}
              onChange={(e) => setConsultantDetails(prev => ({ ...prev, consultant_name: e.target.value }))}
              placeholder="e.g. Adam Klukowski"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="consultant_phone">Phone</Label>
            <Input
              id="consultant_phone"
              value={consultantDetails.consultant_phone}
              onChange={(e) => setConsultantDetails(prev => ({ ...prev, consultant_phone: e.target.value }))}
              placeholder="e.g. 0437 425 132"
            />
          </div>
          <Button onClick={handleSaveConsultantDetails} disabled={savingConsultant}>
            <Save className={`h-4 w-4 mr-2 ${savingConsultant ? 'animate-spin' : ''}`} />
            {savingConsultant ? 'Saving...' : 'Save Consultant Details'}
          </Button>
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>
            Update your password or request a password reset
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={updating || !formData.newPassword || !formData.confirmPassword}
              >
                <Lock className="h-4 w-4 mr-2" />
                Update Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handlePasswordReset}
                disabled={updating}
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Reset Email
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
